/**
 * Site Management Page
 * Admin interface for managing clinical trial sites
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';

interface Site {
  _id: string;
  siteId: string;
  siteName: string;
  institution?: string;
  principalInvestigator: {
    name: string;
    email?: string;
    phone?: string;
  };
  status: 'pending' | 'selected' | 'initiated' | 'enrolling' | 'closed';
  targetEnrollment: number;
  currentEnrollment: number;
  address?: {
    city?: string;
    state?: string;
    country?: string;
  };
}

interface SiteContact {
  name: string;
  role: string;
  email: string;
  phone: string;
  isPrimary: boolean;
}

interface SiteFormData {
  siteId: string;
  siteName: string;
  institution: string;
  principalInvestigator: {
    name: string;
    email: string;
    phone: string;
  };
  contacts: SiteContact[];
  status: string;
  targetEnrollment: number;
  address: {
    street: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
}

const emptyContact: SiteContact = { name: '', role: '', email: '', phone: '', isPrimary: true };

const emptySiteForm: SiteFormData = {
  siteId: '',
  siteName: '',
  institution: '',
  principalInvestigator: { name: '', email: '', phone: '' },
  contacts: [{ ...emptyContact }],
  status: 'pending',
  targetEnrollment: 0,
  address: { street: '', city: '', state: '', postalCode: '', country: 'USA' }
};

const statusColors: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-800',
  selected: 'bg-blue-100 text-blue-800',
  initiated: 'bg-yellow-100 text-yellow-800',
  enrolling: 'bg-green-100 text-green-800',
  closed: 'bg-red-100 text-red-800'
};

const SiteManagement: React.FC = () => {
  const { token, studyId: authStudyId } = useAuth();
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingSite, setEditingSite] = useState<Site | null>(null);
  const [formData, setFormData] = useState<SiteFormData>(emptySiteForm);
  const [error, setError] = useState<string | null>(null);

  // Get the first study (for now, single-study system)
  const { data: studiesData } = useQuery({
    queryKey: ['studies'],
    queryFn: async () => {
      const res = await fetch('/api/studies', {
        headers: { Authorization: `Bearer ${token}` }
      });
      return res.json();
    }
  });

  // Scope to the logged-in study (multi-study safe) instead of the first study
  const studyId = authStudyId || studiesData?.studies?.[0]?._id;

  // Fetch sites
  const { data, isLoading } = useQuery({
    queryKey: ['sites', studyId],
    queryFn: async () => {
      const url = studyId ? `/api/sites?studyId=${studyId}` : '/api/sites';
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return res.json();
    },
    enabled: !!studyId
  });

  // Create/Update site mutation
  const saveMutation = useMutation({
    mutationFn: async (data: SiteFormData & { study: string }) => {
      const url = editingSite ? `/api/sites/${editingSite._id}` : '/api/sites';
      const method = editingSite ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message || 'Failed to save site');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      setShowModal(false);
      setEditingSite(null);
      setFormData(emptySiteForm);
      setError(null);
    },
    onError: (err: Error) => {
      setError(err.message);
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!studyId) {
      setError('No study found');
      return;
    }
    saveMutation.mutate({ ...formData, study: studyId });
  };

  const handleEdit = (site: Site) => {
    setEditingSite(site);
    const siteAny = site as any;
    setFormData({
      siteId: site.siteId,
      siteName: site.siteName,
      institution: site.institution || '',
      principalInvestigator: {
        name: site.principalInvestigator?.name || '',
        email: site.principalInvestigator?.email || '',
        phone: site.principalInvestigator?.phone || ''
      },
      contacts: siteAny.contacts?.length > 0
        ? siteAny.contacts.map((c: any) => ({
            name: c.name || '',
            role: c.role || '',
            email: c.email || '',
            phone: c.phone || '',
            isPrimary: c.isPrimary || false
          }))
        : [{ ...emptyContact }],
      status: site.status,
      targetEnrollment: site.targetEnrollment,
      address: {
        street: site.address?.city || '',
        city: site.address?.city || '',
        state: site.address?.state || '',
        postalCode: '',
        country: site.address?.country || 'USA'
      }
    });
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingSite(null);
    setFormData(emptySiteForm);
    setError(null);
    setShowModal(true);
  };

  const sites: Site[] = data?.sites || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Site Management</h1>
          <p className="text-gray-600">Manage clinical trial sites and their enrollment</p>
        </div>
        <button
          onClick={handleAdd}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Site
        </button>
      </div>

      {/* Sites Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading sites...</div>
        ) : sites.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p>No sites created yet.</p>
            <button onClick={handleAdd} className="mt-2 text-blue-600 hover:underline">
              Create your first site
            </button>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Site ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PI</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Enrollment</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sites.map((site) => (
                <tr key={site._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap font-mono text-sm">{site.siteId}</td>
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{site.siteName}</div>
                    {site.institution && (
                      <div className="text-sm text-gray-500">{site.institution}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {site.principalInvestigator?.name || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">
                    {(() => {
                      const siteAny = site as any;
                      const primary = siteAny.contacts?.find((c: any) => c.isPrimary);
                      const first = siteAny.contacts?.[0];
                      const contact = primary || first;
                      if (!contact?.name) return '-';
                      return (
                        <div>
                          <div>{contact.name}</div>
                          {contact.role && <div className="text-xs text-gray-400">{contact.role}</div>}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${statusColors[site.status]}`}>
                      {site.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <span className="font-medium">{site.currentEnrollment}</span>
                    <span className="text-gray-400"> / {site.targetEnrollment}</span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => handleEdit(site)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold">
                {editingSite ? 'Edit Site' : 'Add New Site'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {error && (
                <div className="p-3 bg-red-50 text-red-700 rounded-lg">{error}</div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Site ID *
                  </label>
                  <input
                    type="text"
                    value={formData.siteId}
                    onChange={(e) => setFormData({ ...formData, siteId: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="001"
                    required
                    disabled={!!editingSite}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="pending">Pending</option>
                    <option value="selected">Selected</option>
                    <option value="initiated">Initiated</option>
                    <option value="enrolling">Enrolling</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Site Name *
                </label>
                <input
                  type="text"
                  value={formData.siteName}
                  onChange={(e) => setFormData({ ...formData, siteName: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="St. Francis Hospital"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Institution
                </label>
                <input
                  type="text"
                  value={formData.institution}
                  onChange={(e) => setFormData({ ...formData, institution: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Catholic Health"
                />
              </div>

              <div className="border-t pt-4">
                <h3 className="font-medium text-gray-900 mb-3">Principal Investigator</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                    <input
                      type="text"
                      value={formData.principalInvestigator.name}
                      onChange={(e) => setFormData({
                        ...formData,
                        principalInvestigator: { ...formData.principalInvestigator, name: e.target.value }
                      })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={formData.principalInvestigator.email}
                      onChange={(e) => setFormData({
                        ...formData,
                        principalInvestigator: { ...formData.principalInvestigator, email: e.target.value }
                      })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={formData.principalInvestigator.phone}
                      onChange={(e) => setFormData({
                        ...formData,
                        principalInvestigator: { ...formData.principalInvestigator, phone: e.target.value }
                      })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-medium text-gray-900">Site Contact(s)</h3>
                  <button
                    type="button"
                    onClick={() => setFormData({
                      ...formData,
                      contacts: [...formData.contacts, { ...emptyContact, isPrimary: false }]
                    })}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    + Add Contact
                  </button>
                </div>
                <p className="text-xs text-gray-500 mb-3">Contact person for the site. Can be the PI or a different person (e.g., coordinator).</p>
                {formData.contacts.map((contact, idx) => (
                  <div key={idx} className="border rounded-lg p-4 mb-3 bg-gray-50">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-600">Contact #{idx + 1}</span>
                      {formData.contacts.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setFormData({
                            ...formData,
                            contacts: formData.contacts.filter((_, i) => i !== idx)
                          })}
                          className="text-xs text-red-500 hover:text-red-700"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                        <input
                          type="text"
                          value={contact.name}
                          onChange={(e) => {
                            const updated = [...formData.contacts];
                            updated[idx] = { ...updated[idx], name: e.target.value };
                            setFormData({ ...formData, contacts: updated });
                          }}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                        <select
                          value={contact.role}
                          onChange={(e) => {
                            const updated = [...formData.contacts];
                            updated[idx] = { ...updated[idx], role: e.target.value };
                            setFormData({ ...formData, contacts: updated });
                          }}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select role...</option>
                          <option value="PI">Principal Investigator</option>
                          <option value="Sub-I">Sub-Investigator</option>
                          <option value="Coordinator">Study Coordinator</option>
                          <option value="Data Manager">Data Manager</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <input
                          type="email"
                          value={contact.email}
                          onChange={(e) => {
                            const updated = [...formData.contacts];
                            updated[idx] = { ...updated[idx], email: e.target.value };
                            setFormData({ ...formData, contacts: updated });
                          }}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                        <input
                          type="tel"
                          value={contact.phone}
                          onChange={(e) => {
                            const updated = [...formData.contacts];
                            updated[idx] = { ...updated[idx], phone: e.target.value };
                            setFormData({ ...formData, contacts: updated });
                          }}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    <label className="flex items-center gap-2 mt-2">
                      <input
                        type="checkbox"
                        checked={contact.isPrimary}
                        onChange={(e) => {
                          const updated = [...formData.contacts];
                          // Only one primary at a time
                          if (e.target.checked) {
                            updated.forEach((c, i) => { updated[i] = { ...c, isPrimary: i === idx }; });
                          } else {
                            updated[idx] = { ...updated[idx], isPrimary: false };
                          }
                          setFormData({ ...formData, contacts: updated });
                        }}
                        className="rounded"
                      />
                      <span className="text-sm text-gray-600">Primary contact</span>
                    </label>
                  </div>
                ))}
              </div>

              <div className="border-t pt-4">
                <h3 className="font-medium text-gray-900 mb-3">Location</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      type="text"
                      value={formData.address.city}
                      onChange={(e) => setFormData({
                        ...formData,
                        address: { ...formData.address, city: e.target.value }
                      })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                    <input
                      type="text"
                      value={formData.address.state}
                      onChange={(e) => setFormData({
                        ...formData,
                        address: { ...formData.address, state: e.target.value }
                      })}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Enrollment
                </label>
                <input
                  type="number"
                  value={formData.targetEnrollment}
                  onChange={(e) => setFormData({ ...formData, targetEnrollment: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  min="0"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingSite(null);
                    setError(null);
                  }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saveMutation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {saveMutation.isPending ? 'Saving...' : (editingSite ? 'Update Site' : 'Create Site')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteManagement;
